import { Typography } from '@material-ui/core';
import React, { useContext, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { AmplitudeEventType } from '../../../analytics/amplitudeEvents';
import { ClientConfig } from '../../../clientConfig';
import { INVALID_CONFIG_ERROR_MESSAGE, MYCHART_UNAVAILABLE_ERROR_MESSAGE, REDIRECT_ERROR } from '../../../types/errorConstants';
import { OPENID_PARAMS } from '../../../types/openid_enum';
import { formatPhoneNumber } from '../../../utils/formatPhoneNumber';
import { ClientConfigContext } from '../../clientConfigContext';
import { useAnalytics } from '../../hooks/useAnalytics';
import { useErrorLogging } from '../../util/errorUtils';
import PromptCard from '../containers/PromptCard';
import VerifyContactCustomerService from '../containers/VerifyContactCustomerService';
import withLogger, { ComponentLoggerProps } from '../HOC/WithLogger';

const PAGE_TYPE = 'type';

function getFallbackErrorMessage(message: string | null): string | undefined {
    if (message === MYCHART_UNAVAILABLE_ERROR_MESSAGE) {
        return 'MyChart is currently unavailable. Please try again later.';
    }

    if (message === INVALID_CONFIG_ERROR_MESSAGE) {
        return 'We could not complete the MyChart redirect. Please contact customer service.';
    }
}

/**
 * The Generic Error Page supports a couple of views.
 * 1) isAzureB2C. Error regarding login attempts and redirects back to the caller.
 * 2) isAIA. For AIA, the language will indicate the "link" is incorrect.
 * 3) For everything else, the generic error page will show the Customer Service support card.
 */
function GeneralErrorPage({ log }: ComponentLoggerProps): JSX.Element {
    // user tracking
    const { sendEvent } = useAnalytics({ eventSource: '', useJWT: false });

    const searchParams = new URLSearchParams(window.location.search);
    const type = searchParams.get(PAGE_TYPE); // isAzureB2C, isAIA or other. Controls page contents.
    const redirectError = searchParams.get('error');
    const fallbackMessage = getFallbackErrorMessage(searchParams.get('message'));
    const clientLandingPageUrl = searchParams.get(OPENID_PARAMS.CANCEL_REDIRECT_URI) as string;

    const clientConfiguration = useContext<ClientConfig>(ClientConfigContext);
    const { t } = useTranslation();
    useErrorLogging(log);

    useEffect(() => {
        if (type === 'isAIA') {
            sendEvent(AmplitudeEventType.IdentityVerification_ContactService);
        }
    }, []);

    const variant = clientLandingPageUrl ? 'closable' : 'default';
    if (type === 'isAzureB2C') {
        return (
            <PromptCard title={'Authentication Error'} closeUrl={clientLandingPageUrl} variant={variant}>
                <Typography variant='h2'>{t('Please wait a few minutes and try again')}.</Typography>
            </PromptCard>
        );
    } else if (redirectError === REDIRECT_ERROR && fallbackMessage) {
        return (
            <PromptCard title={'Redirect Error'} variant='default'>
                <Typography variant='h2'>{t(fallbackMessage)}</Typography>
            </PromptCard>
        );
    } else if (type === 'isAIA') {
        return (
            <PromptCard title={'Contact customer service'} closeUrl={clientLandingPageUrl} variant={variant}>
                <Typography variant='h2'>
                    {t('Your link is not valid. To request a new link, please call customer service toll-free at')}
                    <br />
                    <a href={'tel:' + clientConfiguration.customerSupportNumber}> {formatPhoneNumber(clientConfiguration.customerSupportNumber)} </a>.
                </Typography>
            </PromptCard>
        );
    } else {
        return <VerifyContactCustomerService />;
    }
}

export default withLogger(GeneralErrorPage, { eventSource: 'GeneralErrorPage' });
